import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import authService from '../../services/authService';
import FAQ from '../FAQ';
import FeedbackForm from './FeedbackForm';
import './Docs.css';

const tabs = [
  { key: 'faq', label: 'FAQ' },
  { key: 'learn', label: 'Learn' },
  { key: 'ai-costs', label: 'AI Model Costs' },
  { key: 'terms', label: 'Terms & Conditions' },
  { key: 'privacy', label: 'Privacy Policy' },
  { key: 'disclaimer', label: 'Disclaimer' },
  { key: 'feedback', label: 'Feedback' }
];

const Docs = () => {
  const [activeTab, setActiveTab] = useState('faq');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sections, setSections] = useState([]);
  const [user, setUser] = useState(null);
  const [balanceCents, setBalanceCents] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const isSectionsTab = useMemo(() => activeTab !== 'faq' && activeTab !== 'feedback' && activeTab !== 'learn' && activeTab !== 'ai-costs', [activeTab]);
  // Learn tab state (dynamic docs from Documents table)
  const [learnItems, setLearnItems] = useState([]);
  const [learnLoading, setLearnLoading] = useState(false);
  const [learnError, setLearnError] = useState('');
  const [activeLearnSlug, setActiveLearnSlug] = useState('');
  const [activeLearnDoc, setActiveLearnDoc] = useState(null);
  // AI Costs tab state
  const [aiCosts, setAiCosts] = useState([]);
  const [aiCostsLoading, setAiCostsLoading] = useState(false);
  const [aiCostsError, setAiCostsError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const local = authService.getCurrentUser && authService.getCurrentUser();
        if (local) { setUser(local); return; }
        const info = await authService.getUserInfo().catch(() => null);
        if (info) {
          setUser({ email: info.Email || info.email, name: (info.FirstName || info.firstName || info.Email || info.email || '') });
        } else {
          setUser(null);
        }
      } catch { setUser(null); }
    })();
  }, []);

  const email = useMemo(() => (user?.email || user?.Email || user?.profile?.email || user?.name || '').toString(), [user]);
  const categoryId = useMemo(() => {
    try {
      const em = email.toLowerCase();
      if (!em.startsWith('testdrive') || !em.endsWith('@pocketpromptengineer.com')) return null;
      const between = em.slice('testdrive'.length, em.indexOf('@'));
      const n = parseInt(between, 10);
      if (Number.isNaN(n)) return null;
      if (n <= 9) return 1; // Early Adopters
      if (n <= 79) return 2; // Techies
      if (n <= 89) return 3; // Education
      return 4; // Misc
    } catch { return null; }
  }, [email]);

  useEffect(() => {
    (async () => {
      try {
        if (!email) return;
        const t = authService.getToken && authService.getToken();
        const res = await fetch('/api/billing/balance', { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'include' });
        if (!res.ok) return;
        const j = await res.json();
        if (typeof j?.balanceCents === 'number') setBalanceCents(j.balanceCents);
      } catch {}
    })();
  }, [email]);

  useEffect(() => {
    try {
      const key = email ? `feedbackUnlocked:${email}` : null;
      if (!key) return;
      const stored = localStorage.getItem(key) === '1';
      setUnlocked(stored);
    } catch {}
  }, [email]);

  useEffect(() => {
    try {
      if (!email) return;
      const key = `feedbackUnlocked:${email}`;
      const needed = categoryId === 1 ? 125 : categoryId === 2 ? 50 : categoryId === 3 ? 100 : categoryId === 4 ? 150 : null;
      const eligibleNow = (needed != null) && (typeof balanceCents === 'number') && (balanceCents <= needed);
      if (eligibleNow) {
        localStorage.setItem(key, '1');
        setUnlocked(true);
      }
    } catch {}
  }, [email, categoryId, balanceCents]);

  const shouldShowFeedback = useMemo(() => {
    try {
      const em = email.toLowerCase();
      return em.startsWith('testdrive') && em.endsWith('@pocketpromptengineer.com');
    } catch { return false; }
  }, [email]);

  const renderWithSlashBold = (text) => {
    if (!text || typeof text !== 'string') return text;
    const result = [];
    const regex = /\/\/(.*?)\/\//g;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        result.push(text.slice(lastIndex, match.index));
      }
      result.push(<strong key={`b-${result.length}`}>{match[1]}</strong>);
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
      result.push(text.slice(lastIndex));
    }
    return result;
  };

  useEffect(() => {
    if (!isSectionsTab || activeTab === 'learn') return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await fetch(`/api/docs/${activeTab}`);
        if (!resp.ok) throw new Error(`Failed to load ${activeTab}`);
        const data = await resp.json();
        if (!cancelled) setSections(Array.isArray(data.sections) ? data.sections : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [activeTab, isSectionsTab]);

  // Load Learn list
  useEffect(() => {
    if (activeTab !== 'learn') return;
    let cancelled = false;
    const run = async () => {
      setLearnLoading(true);
      setLearnError('');
      try {
        const resp = await fetch('/api/docs?type=AppInfo');
        if (!resp.ok) throw new Error('Failed to load docs');
        const data = await resp.json();
        const items = Array.isArray(data.items) ? data.items : [];
        if (!cancelled) {
          setLearnItems(items);
          if (items.length > 0) {
            const first = items[0];
            setActiveLearnSlug(first.slug || first.Slug || '');
          } else {
            setActiveLearnSlug('');
            setActiveLearnDoc(null);
          }
        }
      } catch (e) {
        if (!cancelled) setLearnError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLearnLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [activeTab]);

  // Load selected Learn doc
  useEffect(() => {
    if (activeTab !== 'learn') return;
    if (!activeLearnSlug) { setActiveLearnDoc(null); return; }
    let cancelled = false;
    const run = async () => {
      try {
        const resp = await fetch(`/api/docs/${activeLearnSlug}`);
        if (!resp.ok) throw new Error('Failed to load document');
        const data = await resp.json();
        if (!cancelled) setActiveLearnDoc(data?.doc || data || null);
      } catch (e) {
        if (!cancelled) setActiveLearnDoc({ title: 'Error', content: e.message || 'Failed to load' });
      }
    };
    run();
    return () => { cancelled = true; };
  }, [activeTab, activeLearnSlug]);

  // Load AI Model Costs on tab activation - uses public endpoint (no auth required)
  useEffect(() => {
    if (activeTab !== 'ai-costs') return;
    let cancelled = false;
    const run = async () => {
      setAiCostsLoading(true);
      setAiCostsError('');
      try {
        const resp = await fetch('/api/aipricing/public');
        if (!resp.ok) throw new Error('Failed to load AI model costs');
        const data = await resp.json();
        const rows = Array.isArray(data) ? data : [];
        // Normalize and sort: cheapest output first, then cheapest input
        const norm = rows.map(r => ({
          provider: r.Provider ?? r.provider ?? '',
          model: r.Model ?? r.model ?? '',
          inPer1k: Number(r.InputPricePer1K ?? r.inputPricePer1K ?? 0),
          outPer1k: Number(r.OutputPricePer1K ?? r.outputPricePer1K ?? 0)
        }));
        norm.sort((a, b) => (a.outPer1k - b.outPer1k) || (a.inPer1k - b.inPer1k) || a.provider.localeCompare(b.provider) || a.model.localeCompare(b.model));
        if (!cancelled) setAiCosts(norm);
      } catch (e) {
        if (!cancelled) setAiCostsError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setAiCostsLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [activeTab]);

  return (
    <div className="docs-container">
      <div className="docs-header">
        <h1>Docs</h1>
        <div className="docs-tabs">
          {tabs.filter(t => t.key !== 'feedback' || shouldShowFeedback).map(t => (
            <button
              key={t.key}
              className={`docs-tab ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
          <Link
            to="/pricing"
            className="docs-tab"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            Pricing
          </Link>
        </div>
      </div>

      <div className="docs-content">
        {activeTab === 'faq' && (
          <FAQ />
        )}

        {activeTab === 'feedback' && shouldShowFeedback && (
          <div style={{ maxWidth: 980 }}>
            <FeedbackForm user={user} />
          </div>
        )}

        {activeTab === 'feedback' && !shouldShowFeedback && (
          <div className="docs-empty">Feedback is not available for this account.</div>
        )}

        {isSectionsTab && (
          <div className="docs-sections">
            {loading && <div className="docs-loading">Loading…</div>}
            {error && (
              <div className="docs-error">
                {error}
                <button className="docs-retry" onClick={() => setActiveTab(activeTab)}>Retry</button>
              </div>
            )}
            {!loading && !error && sections.length === 0 && (
              <div className="docs-empty">No content available.</div>
            )}
            {!loading && !error && sections.length > 0 && (
              <div className="docs-sections-list">
                {sections.map((s) => (
                  <div key={s.id || `${s.title}-${s.displayOrder}`} className="doc-section">
                    <div className="doc-title"><strong>{s.title}</strong></div>
                    <div className="doc-gap" />
                    <div className="doc-content">{renderWithSlashBold(s.content)}</div>
                    <div className="doc-record-gap" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'learn' && (
          <div className="docs-sections" style={{ display:'grid', gridTemplateColumns:'260px 1fr', gap:16 }}>
            <div style={{ borderRight:'1px solid #eee', paddingRight:12 }}>
              <div style={{ fontWeight:600, marginBottom:8 }}>Learn</div>
              {learnLoading && <div className="docs-loading">Loading…</div>}
              {learnError && <div className="docs-error">{learnError}</div>}
              {!learnLoading && !learnError && learnItems.length === 0 && (
                <div className="docs-empty">No docs yet.</div>
              )}
              {!learnLoading && !learnError && learnItems.length > 0 && (
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {learnItems.map(it => {
                    const slug = it.slug || it.Slug;
                    const title = it.title || it.Title || slug;
                    const active = slug === activeLearnSlug;
                    return (
                      <button key={slug} onClick={() => setActiveLearnSlug(slug)}
                        className={`docs-tab ${active ? 'active' : ''}`} style={{ textAlign:'left' }}>
                        {title}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              {activeLearnDoc ? (
                <div>
                  <div className="doc-title" style={{ fontSize:'1.2rem', fontWeight:700 }}>{activeLearnDoc.title || activeLearnDoc.Title || ''}</div>
                  <div className="doc-gap" />
                  <div className="doc-content" style={{ whiteSpace:'pre-wrap' }}>{activeLearnDoc.content || activeLearnDoc.Content || ''}</div>
                </div>
              ) : (
                <div className="docs-empty">Select a document</div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'ai-costs' && (
          <div className="docs-sections">
            <div style={{ fontWeight:600, marginBottom:8 }}>AI Model Costs (per 1K tokens)</div>
            {aiCostsLoading && <div className="docs-loading">Loading…</div>}
            {aiCostsError && <div className="docs-error">{aiCostsError}</div>}
            {!aiCostsLoading && !aiCostsError && aiCosts.length === 0 && (
              <div className="docs-empty">No pricing data available.</div>
            )}
            {!aiCostsLoading && !aiCostsError && aiCosts.length > 0 && (
              <div style={{ overflowX:'auto' }}>
                <table className="docs-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign:'left', paddingRight:50 }}>Provider</th>
                      <th style={{ textAlign:'left' }}>Model</th>
                      <th style={{ textAlign:'right', paddingRight:50 }}>Tokens In Cost/1k</th>
                      <th style={{ textAlign:'right' }}>Tokens Out Cost/1k</th>
                      <th style={{ textAlign:'left', paddingLeft:40 }}>Rank</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aiCosts.map((r, idx) => {
                      const isFirst = idx === 0;
                      const isLast = idx === aiCosts.length - 1;
                      const label = isFirst ? 'Lowest Cost' : (isLast ? 'Highest Cost' : '');
                      const labelColor = isFirst ? '#2e7d32' : '#b71c1c';
                      return (
                        <tr key={`${r.provider}-${r.model}-${idx}`}>
                          <td style={{ paddingRight:50 }}>{r.provider}</td>
                          <td>{r.model}</td>
                          <td style={{ textAlign:'right', paddingRight:50 }}>${r.inPer1k.toFixed(6)}</td>
                          <td style={{ textAlign:'right' }}>${r.outPer1k.toFixed(6)}</td>
                          <td style={{ paddingLeft:40, color: label ? labelColor : undefined, fontWeight: label ? 600 : undefined }}>
                            {label}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Docs;


